import { memberId } from '@trello/session-cookie';
import { useCallback, useMemo } from 'react';
import { useAddOneTimeMessageDismissedMutation } from './AddOneTimeMessageDismissedMutation.generated';
import { useMyOneTimeMessagesDismissedQuery } from './MyOneTimeMessagesDismissedQuery.generated';

export const useOneTimeMessagesDismissed = () => {
  const { data } = useMyOneTimeMessagesDismissedQuery({
    variables: { idMember: memberId || 'me' },
    fetchPolicy: 'cache-only',
    waitOn: ['MemberHeader'],
  });
  const [addOneTimeMessageDismissed] = useAddOneTimeMessageDismissedMutation();

  const oneTimeMessagesDismissed = useMemo(
    () => data?.member?.oneTimeMessagesDismissed ?? [],
    [data?.member?.oneTimeMessagesDismissed],
  );

  const isOneTimeMessageDismissed = useCallback(
    (messageId: string) =>
      oneTimeMessagesDismissed.some((message) => message === messageId),
    [oneTimeMessagesDismissed],
  );

  const dismissOneTimeMessage = useCallback(
    async (
      messageId: string,
      options: { optimistic?: boolean } = { optimistic: true },
    ) => {
      if (isOneTimeMessageDismissed(messageId)) {
        return;
      }

      return addOneTimeMessageDismissed({
        variables: {
          memberId: memberId || 'me',
          messageId,
        },
        optimisticResponse: options?.optimistic
          ? {
              __typename: 'Mutation',
              addOneTimeMessagesDismissed: {
                id: 'me',
                oneTimeMessagesDismissed: oneTimeMessagesDismissed?.concat([
                  messageId,
                ]),
                __typename: 'Member',
              },
            }
          : undefined,
      });
    },
    [
      addOneTimeMessageDismissed,
      oneTimeMessagesDismissed,
      isOneTimeMessageDismissed,
    ],
  );

  return {
    oneTimeMessagesDismissed,
    dismissOneTimeMessage,
    isOneTimeMessageDismissed,
  };
};
